// ============================================================
// MULTI-CONTRACTOR ASSIGN MODAL
// Select multiple contractors, send request via single action
// ============================================================

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Users, Building2, Phone, Mail, Loader2 } from 'lucide-react';
import { runJobAction, LHS_API } from '../utils/lhsCore';

export default function AssignContractorModal({ 
  job, 
  isOpen,
  onClose, 
  onJobUpdated,
  onError,
}) {
  const [contractors, setContractors] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingContractors, setLoadingContractors] = useState(true);

  // Load contractors on mount
  useEffect(() => {
    if (isOpen) {
      loadContractors();
    }
  }, [isOpen]);

  const loadContractors = async () => {
    setLoadingContractors(true);
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_BASE}/api/wyldecrest/contractors`);
      if (res.ok) {
        const data = await res.json();
        setContractors(data.contractors || []);
      }
    } catch (error) {
      console.error('Failed to load contractors:', error);
    } finally {
      setLoadingContractors(false);
    }
  };

  const toggle = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!selected.length || !job) return;

    setLoading(true);
    try {
      // Build contractor names map
      const contractor_names = {};
      contractors.forEach(c => {
        if (selected.includes(c.id)) {
          contractor_names[c.id] = c.name || c.company_name;
        }
      });

      const updatedJob = await runJobAction(
        LHS_API.assignContractors(job.id),
        {
          contractor_ids: selected,
          contractor_names,
        }
      );

      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }

      onClose();
    } catch (error) {
      if (onError) {
        onError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#174C86] to-[#1e5a9e] p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Assign Contractors</h2>
                  <p className="text-sm text-white/70">{job?.job_ref} - {job?.park_name}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {loadingContractors ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : contractors.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No contractors available
              </div>
            ) : (
              <div className="space-y-2 max-h-[360px] overflow-y-auto">
                {contractors.map((contractor) => {
                  const isSelected = selected.includes(contractor.id);
                  return (
                    <motion.button
                      key={contractor.id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => toggle(contractor.id)}
                      data-testid={`contractor-option-${contractor.id}`}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'bg-blue-50 border-blue-500 shadow-md'
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Selection indicator */}
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-blue-500' : 'bg-gray-100'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>

                        {/* Contractor info */}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {contractor.name || contractor.company_name}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {contractor.specialization && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {contractor.specialization}
                              </span>
                            )}
                            {contractor.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {contractor.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-5 bg-gray-50 border-t">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              disabled={!selected.length || loading}
              onClick={handleSubmit}
              data-testid="assign-submit-btn"
              className="w-full py-3.5 rounded-xl bg-[#174C86] hover:bg-[#1a5694] text-white font-semibold 
                         disabled:opacity-40 disabled:cursor-not-allowed transition-all
                         flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  Send Request ({selected.length})
                </>
              )}
            </motion.button>

            <button
              onClick={onClose}
              className="mt-2 w-full py-2.5 rounded-xl border border-gray-300 text-gray-600 
                         hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
